import React, { ReactNode } from 'react';
import { Box, Typography, Fab, useMediaQuery, useTheme } from '@mui/material';

interface PrimaryActionProps {
  icon: ReactNode;
  onClick: () => void;
  label?: string;
}

export type { PrimaryActionProps };

interface PageSectionProps {
  icon?: string;
  title: string;
  subtitle?: string;
  description?: string;
  primaryAction?: PrimaryActionProps;
  secondaryActions?: ReactNode;
  children: ReactNode;
  variant?: 'default' | 'compact';
  titleVariant?: 'h3' | 'h4' | 'h5' | 'h6';
}

const PageSection: React.FC<PageSectionProps> = ({
  icon,
  title,
  subtitle,
  description,
  primaryAction,
  secondaryActions,
  children,
  variant = 'default',
  titleVariant = 'h4'
}) => {
  const isCompact = variant === 'compact';
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: isCompact ? 2 : 3, position: 'relative' }}>
      {/* Header Section */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2 }}>
        <Box sx={{ flex: 1 }}>
          <Typography 
            variant={titleVariant} 
            gutterBottom 
            sx={{ 
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              mb: isCompact ? 1 : 2
            }}
          >
            {icon && <span>{icon}</span>}
            {title}
          </Typography>
          
          {subtitle && (
            <Typography 
              variant={isCompact ? "body2" : "body1"} 
              color="text.secondary" 
              gutterBottom
            >
              {subtitle}
            </Typography>
          )}
          
          {description && (
            <Typography 
              variant="body2" 
              color="text.secondary" 
              sx={{ mt: isCompact ? 0.5 : 1 }}
            >
              {description}
            </Typography>
          )}
        </Box>
        
        {secondaryActions && (
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 1, 
            flexShrink: 0,
            flexWrap: 'wrap'
          }}>
            {secondaryActions}
          </Box>
        )}
      </Box>

      {/* Content Section */}
      <Box>
        {children}
      </Box>

      {/* Floating Action Button */}
      {primaryAction && (
        <Fab
          variant={isMobile ? "circular" : "extended"}
          sx={{
            position: 'fixed',
            bottom: 24,
            right: 24,
            zIndex: 1000
          }}
          color="primary"
          aria-label={primaryAction.label}
          onClick={primaryAction.onClick}
        >
          {primaryAction.icon}
          {!isMobile && primaryAction.label && (
            <Box component="span" sx={{ ml: 1 }}>
              {primaryAction.label}
            </Box>
          )}
        </Fab>
      )}
    </Box>
  );
};

export default PageSection;
